import { Box, Text } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { IntegrationsSyncRunSyncingDocument } from 'generated/resources/graphql'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { aggregateStatuses } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { useIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

export const Syncing = () => {
  const { syncLoading, lastSyncRun, configuration } = useIntegrationsSyncContext()
  const [
    fetchSyncRunSyncing,
    { data: syncRunSyncingData, loading: syncRunSyncingLoading, fetchMore },
  ] = useLazyQuery(IntegrationsSyncRunSyncingDocument)

  const lastSyncRunId = lastSyncRun?.id
  const includeSyncable = !!configuration.syncableStatesAggregation

  const pageInfo = syncRunSyncingData?.integrations?.syncableSyncRunsConnection?.pageInfo
  const hasNextPage = pageInfo?.hasNextPage ?? false
  const endCursor = pageInfo?.endCursor

  const fetchMoreCallback = useCallback(async () => {
    await fetchMore({
      variables: {
        syncRunId: lastSyncRunId,
        includeSyncable,
        after: endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.integrations?.syncableSyncRunsConnection) {
          return previousResult
        }

        const previousNodes = previousResult.integrations?.syncableSyncRunsConnection?.nodes ?? []
        const newNodes = fetchMoreResult.integrations.syncableSyncRunsConnection.nodes ?? []

        return {
          ...previousResult,
          integrations: {
            ...previousResult.integrations,
            syncableSyncRunsConnection: {
              ...fetchMoreResult.integrations.syncableSyncRunsConnection,
              nodes: [...previousNodes, ...newNodes],
            },
          },
        }
      },
    })
  }, [endCursor, lastSyncRunId, includeSyncable])

  useEffect(() => {
    if (!lastSyncRunId) return

    fetchSyncRunSyncing({
      variables: {
        syncRunId: lastSyncRunId,
        includeSyncable,
      },
    })
  }, [lastSyncRunId, includeSyncable])

  if (!lastSyncRunId) return null

  if (hasNextPage) {
    fetchMoreCallback()
  }

  const syncableStates = aggregateStatuses(
    syncRunSyncingData?.integrations?.syncableSyncRunsConnection?.nodes.map(
      (node) => node.syncableState
    ) ?? [],
    configuration.syncableStatesAggregation
  )

  const counter =
    syncLoading || syncRunSyncingLoading
      ? ''
      : `${syncableStates.filter((status) => !status.has('syncing')).length} / ${
          syncableStates.length
        }`

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <Spinner size="small" />
      <Text.Normal color="grey800" size="200" weight="medium">
        {`${i18n.t('integrations.syncable_status.syncing')} ${counter}`}
      </Text.Normal>
    </Box>
  )
}
